import { ParsedUrlQuery } from "querystring"
import * as F from "fp-ts/function"
import * as O from "fp-ts/Option"
import * as ROA from "fp-ts/ReadonlyArray"
import { AirParams, AirParamsFlight } from "@ssc-booking/api-contracts"
import * as AP from "fp-ts/Apply"
import { NormalQueryParam, removeFromQuery } from "../../_common/routing"
import { encodeGuests } from "../../_common/guests-encoding"
import { encodeAirFare } from "../../_common/airfare-encoding"
import { encodeGateway } from "../../_common/gateway-encoding"
import { encodeVs } from "../../_common/vs-encoding"
import { encodeSuite } from "../../_common/suite-encoding"
import { checkoutId } from "./machines/ecommerce-machine/e-commerce-machine-helpers"
import { FareCard, FareCardKey } from "./read-models/fare-card"

import { ECommerceContext } from "./machines/ecommerce-machine/e-commerce-machine-context"

type ContextForQueryParams = Pick<
    ECommerceContext,
    | "guests"
    | "selectedFare"
    | "vsCode"
    | "selectedSuiteCategory"
    | "selectedSuiteCategorySource"
    | "selectedSuiteNumber"
    | "checkoutStatus"
    | "sessionId"
> & { fareCards: readonly Pick<FareCard, "key" | "air">[] }

export const presentParamsFromContext = (ctx: ContextForQueryParams) =>
    Object.fromEntries(Object.entries(paramsFromContext(ctx)).filter(paramIsPresent))

export const notPresentParamsFromContext = (ctx: ContextForQueryParams) =>
    Object.keys(Object.fromEntries(Object.entries(paramsFromContext(ctx)).filter(paramIsNotPresent)))

export const setQueryParams = (url: URL, params: Record<string, string | string[]>) => {
    Object.entries(params).forEach(([key, value]) => {
        if (Array.isArray(value)) value.forEach((v) => url.searchParams.append(key, v))
        else url.searchParams.set(key, value)
    })
}

const sequenceO = AP.sequenceS(O.Apply)

type NormalQueryParamWithoutModal = Exclude<NormalQueryParam, "modal">

const paramsFromContext = (
    ctx: ContextForQueryParams,
): Record<NormalQueryParamWithoutModal, string | undefined | string[]> => {
    const maybeFlight = firstAirParamWithFlight(ctx.fareCards)

    return {
        guests: encodeGuests(ctx.guests),
        fare: ctx.selectedFare?.key,
        airfare: F.pipe(maybeFlight, encodeAirFare, O.toUndefined),
        gateway: F.pipe(maybeFlight, encodeGateway, O.toUndefined),
        vs: encodeVs(ctx.vsCode),
        suite: F.pipe(
            sequenceO({
                suiteCategory: O.fromNullable(ctx.selectedSuiteCategory),
                source: O.fromNullable(ctx.selectedSuiteCategorySource),
            }),
            O.map(encodeSuite),
            O.toUndefined,
        ),
        "suite-number": ctx.selectedSuiteNumber,
        checkout: checkoutId(ctx),
    }
}

export const paramsFromQuery = (query: ParsedUrlQuery) => removeFromQuery(query, "modal")

const paramIsPresent = (entity: [string, string | undefined | string[]]): entity is [string, string | string[]] =>
    Array.isArray(entity[1]) ? entity[1].length > 0 : !!entity[1]
const paramIsNotPresent = (entity: [string, string | undefined | string[]]): entity is [string, string | string[]] =>
    Array.isArray(entity[1]) ? entity[1].length === 0 : !entity[1]

type FareKeyAndAir = { key: FareCardKey; air: AirParamsFlight }
const firstAirParamWithFlight = (fareCards: readonly Pick<FareCard, "key" | "air">[]): O.Option<FareKeyAndAir> =>
    F.pipe(
        fareCards,
        ROA.map((x) => ({ key: x.key, air: x.air })),
        ROA.findFirst((x): x is FareKeyAndAir => AirParams.is.flight(x.air)),
    )
